// src/pages/FulfillmentPage/components/ShippingInfo.tsx
import React from "react";
import { Box, Typography, Stack, Button } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/Print";
import { Fulfillment } from "../types";
import { FulfillmentLabel } from "../../../api/fulfillmentApi";

interface ShippingInfoProps {
  fulfillment: Fulfillment;
  latestLabel: FulfillmentLabel | null;
  labelHistory?: FulfillmentLabel[];
  isShipping: boolean;
  hasLabelRecord: boolean;
  hasLabelUrl: boolean;
  actionLoading?: boolean;
  onRefundLabel?: () => void;
}

export const ShippingInfo: React.FC<ShippingInfoProps> = ({
  fulfillment,
  latestLabel,
  labelHistory = [],
  isShipping,
  hasLabelRecord,
  hasLabelUrl,
  actionLoading,
  onRefundLabel,
}) => {
  if (!isShipping || (fulfillment.status !== "SHIPPED" && !hasLabelRecord)) {
    return null;
  }

  const trackUrl = fulfillment.tracking_url ?? latestLabel?.tracking_url ?? undefined;
  const labelUrl = latestLabel?.label_url ?? undefined;
  const canRefundLabel = fulfillment.status === "READY" && !!latestLabel?.id && !!onRefundLabel;

  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
      <Typography variant="caption" color="text.secondary" gutterBottom>
        Shipping
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        {fulfillment.carrier || latestLabel?.carrier || "Carrier"} —{" "}
        {fulfillment.tracking_number || latestLabel?.tracking_number || "No tracking"}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap">
        {!!trackUrl && (
          <Button
            component="a"
            href={trackUrl}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon fontSize="small" />}
          >
            Track
          </Button>
        )}

        {!!labelUrl ? (
          <Button
            component="a"
            href={labelUrl}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant={fulfillment.status === "SHIPPED" ? "outlined" : "contained"}
            color={fulfillment.status === "SHIPPED" ? "primary" : "success"}
            startIcon={<PrintIcon fontSize="small" />}
          >
            Print Label
          </Button>
        ) : latestLabel ? (
          <Button
            size="small"
            variant="contained"
            disabled
            startIcon={<PrintIcon fontSize="small" />}
          >
            Label generating…
          </Button>

        ) : null}
        {canRefundLabel && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={!!actionLoading}
            onClick={onRefundLabel}
            startIcon={<ReplayIcon fontSize="small" />}
          >
            Refund / Retry Label
          </Button>
        )}
      </Stack>

      {hasLabelRecord && !hasLabelUrl && fulfillment.status !== "SHIPPED" && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Label purchased — generating PDF…
        </Typography>
      )}

      {hasLabelUrl && fulfillment.status !== "SHIPPED" && (
        <Typography variant="caption" color="success.main" sx={{ display: "block", mt: 1 }}>
          ✓ Label purchased. Click “Mark Shipped” when ready to ship.
        </Typography>
      )}
      {labelHistory.length > 0 && (
  <Box sx={{ mt: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
      Label / Refund History
    </Typography>

    <Stack spacing={1}>
      {labelHistory.map((label) => {
        const refundStatus = label.raw?.refund?.status || null;
        const refundUpdated = label.raw?.refund?.object_updated || label.raw?.refund_last_checked_at || null;

        return (
          <Box
            key={label.id}
            sx={{
              p: 1,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
              Label #{label.id} — {label.status || "unknown"}
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {label.carrier || "Carrier"} • {label.tracking_number || "No tracking"}
            </Typography>

            {refundStatus && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block" }}>
                Refund status: {refundStatus}
                {refundUpdated ? ` • Updated ${new Date(refundUpdated).toLocaleString()}` : ""}
              </Typography>
            )}

            {label.status?.includes("refund") && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Carrier policy applies. Refund approval is not always instant.
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  </Box>
)}
    </Box>
  );
};